import { TaskStatus } from '../../types';
import { ValidatorComment } from '../../services/taskValidator';

export type ValidatorStatus = 'idle' | 'running' | 'done' | 'error';

export interface ValidationControllerPorts {
  validate: (taskId: string) => Promise<ValidatorComment[]>;
  reportError?: (error: Error, taskId: string) => void;
}

export interface ValidationControllerSnapshot {
  commentsByField: Map<string, ValidatorComment[]>;
  dismissedIds: Set<string>;
  status: ValidatorStatus;
  error: string | null;
}

function asError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

/**
 * Owns the validator's transient session. Results are scoped to the current
 * task generation; changing tasks or cancelling a run invalidates late model
 * responses without affecting the next task's state.
 */
export class ValidationController {
  private readonly ports: ValidationControllerPorts;

  private snapshot: ValidationControllerSnapshot = {
    commentsByField: new Map(),
    dismissedIds: new Set(),
    status: 'idle',
    error: null,
  };

  private listeners = new Set<() => void>();

  private taskId: string | undefined;

  private taskStatus: TaskStatus | undefined;

  private generation = 0;

  private disposed = false;

  constructor(ports: ValidationControllerPorts) {
    this.ports = ports;
  }

  getSnapshot = (): ValidationControllerSnapshot => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  initialize(): void {
    this.disposed = false;
  }

  updateContext(taskId: string | undefined, status: TaskStatus | undefined): void {
    const taskChanged = taskId !== this.taskId;
    const wasTerminal = this.isTerminal(this.taskStatus);
    const enteredTerminalState = (status === 'approved' || status === 'archived') && !wasTerminal;
    this.taskId = taskId;
    this.taskStatus = status;
    if (!taskChanged && !enteredTerminalState) return;
    this.generation += 1;
    this.resetSnapshot();
  }

  async run(): Promise<boolean> {
    if (this.disposed || this.snapshot.status === 'running' || !this.taskId) return false;
    const taskId = this.taskId;
    const generation = this.generation;
    this.snapshot = {
      ...this.snapshot,
      status: 'running',
      error: null,
    };
    this.emit();
    try {
      const comments = await this.ports.validate(taskId);
      if (!this.isCurrent(generation, taskId)) return false;
      const grouped = new Map<string, ValidatorComment[]>();
      comments.forEach((comment) => {
        const list = grouped.get(comment.fieldPath) ?? [];
        list.push(comment);
        grouped.set(comment.fieldPath, list);
      });
      this.snapshot = {
        commentsByField: grouped,
        dismissedIds: new Set(),
        status: 'done',
        error: null,
      };
      this.emit();
      return true;
    } catch (error) {
      if (!this.isCurrent(generation, taskId)) return false;
      const normalized = asError(error);
      this.snapshot = {
        ...this.snapshot,
        status: 'error',
        error: normalized.message,
      };
      this.ports.reportError?.(normalized, taskId);
      this.emit();
      return false;
    }
  }

  /**
   * Load comments the controller did not fetch itself, presenting them as a
   * completed run so the verification rail renders them exactly like a
   * Validate click would.
   *
   * Used when the submit command refuses a submission for AI-detected
   * content: the server already ran detection and returned the findings, so
   * re-running the validator would be a second paid round trip for the same
   * answer.
   */
  showComments(comments: ValidatorComment[]): void {
    if (this.disposed || !this.taskId) return;
    const grouped = new Map<string, ValidatorComment[]>();
    comments.forEach((comment) => {
      const list = grouped.get(comment.fieldPath) ?? [];
      list.push(comment);
      grouped.set(comment.fieldPath, list);
    });
    this.snapshot = {
      commentsByField: grouped,
      dismissedIds: new Set(),
      status: 'done',
      error: null,
    };
    this.emit();
  }

  cancel(): void {
    if (this.snapshot.status !== 'running') return;
    this.generation += 1;
    this.resetSnapshot();
  }

  dismiss(fieldPath: string, originalIndex: number): void {
    const next = new Set(this.snapshot.dismissedIds);
    next.add(`${fieldPath}::${originalIndex}`);
    this.snapshot = { ...this.snapshot, dismissedIds: next };
    this.emit();
  }

  restore(fieldPath: string, originalIndex: number): void {
    const next = new Set(this.snapshot.dismissedIds);
    next.delete(`${fieldPath}::${originalIndex}`);
    this.snapshot = { ...this.snapshot, dismissedIds: next };
    this.emit();
  }

  clear(): void {
    this.generation += 1;
    this.resetSnapshot();
  }

  dispose(): void {
    this.generation += 1;
    this.disposed = true;
    this.resetSnapshot();
  }

  private resetSnapshot(): void {
    this.snapshot = {
      commentsByField: new Map(),
      dismissedIds: new Set(),
      status: 'idle',
      error: null,
    };
    this.emit();
  }

  private isCurrent(generation: number, taskId: string): boolean {
    return !this.disposed
      && generation === this.generation
      && taskId === this.taskId;
  }

  private isTerminal(status: TaskStatus | undefined): boolean {
    return status === 'approved' || status === 'archived';
  }

  private emit(): void {
    this.listeners.forEach((listener) => listener());
  }
}
